import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import styles from "./Modal.module.css";

export interface ModalProps {
	/** Whether the modal is currently visible. */
	open: boolean;
	/** Called when the user dismisses (Escape, backdrop click, or X button). */
	onClose: () => void;
	/** Accessible title — required. Rendered visibly unless `hideTitle` is set. */
	title: string;
	/** Optional description for screen readers. Rendered visibly unless `hideDescription`. */
	description?: string;
	/** Modal body. */
	children: ReactNode;
	/** Optional action bar (buttons) shown at the bottom of the modal. */
	footer?: ReactNode;
	/** Max width CSS value. Default: min(90vw, 560px). */
	maxWidth?: string;
	/** Hide the visible title (still exposed to screen readers via aria-labelledby). */
	hideTitle?: boolean;
	/** Hide the close button in the top-right corner. */
	hideCloseButton?: boolean;
	/** Prevent dismissal on backdrop click. Useful for destructive confirms. */
	dismissOnBackdrop?: boolean;
}

/**
 * Accessible modal dialog.
 *
 * - Traps focus within the dialog while open.
 * - Closes on Escape, backdrop click, or close button.
 * - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` + `aria-describedby`.
 * - Mobile-safe via the global `[role="dialog"]` max-width cap in globals.css.
 *
 * Example:
 *   <Modal open={show} onClose={() => setShow(false)} title="Delete project">
 *     <p>This cannot be undone.</p>
 *   </Modal>
 */
export function Modal({
	open,
	onClose,
	title,
	description,
	children,
	footer,
	maxWidth,
	hideTitle = false,
	hideCloseButton = false,
	dismissOnBackdrop = true,
}: ModalProps) {
	const panelRef = useRef<HTMLDivElement>(null);
	const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2, 9)}`);
	const descId = useRef(`modal-desc-${Math.random().toString(36).slice(2, 9)}`);

	// Keep a stable ref to onClose so the effect doesn't re-run on every render
	// (inline arrow functions like `() => setShow(false)` create new references each render,
	// which would re-trigger the effect and steal focus from inputs mid-keystroke).
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	const stableOnClose = useCallback(() => onCloseRef.current(), []);

	// Close on Escape + basic focus trap
	useEffect(() => {
		if (!open) return;

		const previouslyFocused = document.activeElement as HTMLElement | null;

		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				stableOnClose();
				return;
			}
			if (e.key !== "Tab" || !panelRef.current) return;

			const focusable = panelRef.current.querySelectorAll<HTMLElement>(
				'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
			);
			if (focusable.length === 0) return;
			const first = focusable[0]!;
			const last = focusable[focusable.length - 1]!;
			const active = document.activeElement;

			if (e.shiftKey && active === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && active === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKey);

		// Move focus into the panel on open
		const timer = window.setTimeout(() => {
			panelRef.current?.querySelector<HTMLElement>(
				'button, [tabindex="0"], input, textarea, select',
			)?.focus();
		}, 0);

		return () => {
			document.removeEventListener("keydown", handleKey);
			window.clearTimeout(timer);
			// Restore focus to the element that opened the modal
			previouslyFocused?.focus?.();
		};
	}, [open, stableOnClose]);

	if (!open) return null;

	const style = maxWidth ? { maxWidth } : undefined;

	return (
		<div
			className={styles.backdrop}
			onClick={dismissOnBackdrop ? onClose : undefined}
			role="presentation"
		>
			<div
				ref={panelRef}
				className={styles.panel}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId.current}
				aria-describedby={description ? descId.current : undefined}
				onClick={(e) => e.stopPropagation()}
				style={style}
			>
				<div className={styles.header}>
					<h2
						id={titleId.current}
						className={`${styles.title} ${hideTitle ? styles.srOnly : ""}`}
					>
						{title}
					</h2>
					{!hideCloseButton && (
						<button
							type="button"
							className={styles.close}
							onClick={onClose}
							aria-label="Close dialog"
						>
							<X size={16} />
						</button>
					)}
				</div>
				{description && (
					<p id={descId.current} className={styles.description}>
						{description}
					</p>
				)}
				<div className={styles.body}>{children}</div>
				{footer && <div className={styles.footer}>{footer}</div>}
			</div>
		</div>
	);
}
